import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointmentTypes } from '@features/appointment-types';
import { useCreateEncounterMutation } from '@features/encounters';
import { SCHEDULED_APPOINTMENT_REQUIRED_MESSAGE, useNoteCreationPolicy } from '@features/user';
import { useSnackbar } from 'notistack';

export const useRecordNowAction = (patientId: string) => {
  const { appointmentTypes } = useAppointmentTypes();
  const [createEncounter, { isLoading }] = useCreateEncounterMutation();
  const { requiresScheduledAppointmentForNoteCreation } = useNoteCreationPolicy();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const recordNow = useCallback(async () => {
    if (requiresScheduledAppointmentForNoteCreation) {
      enqueueSnackbar(SCHEDULED_APPOINTMENT_REQUIRED_MESSAGE, { variant: 'warning' });
      navigate('/schedule');
      return;
    }

    try {
      const defaultAppointmentType = appointmentTypes?.[0];
      const encounter = await createEncounter({
        createEncounterRequest: {
          patient_id: patientId,
          apply_defaults: true,
          encounter_type: defaultAppointmentType?.encounter_type,
          template_id: defaultAppointmentType?.template_id ?? undefined,
        },
      }).unwrap();
      navigate(`/encounters/${encounter.encounter_id}`, {
        state: { appointmentTypeSuggested: Boolean(defaultAppointmentType) },
      });
    } catch (error) {
      console.error('Error creating encounter:', error);
      enqueueSnackbar('Failed to create encounter', { variant: 'error' });
    }
  }, [
    appointmentTypes,
    createEncounter,
    enqueueSnackbar,
    navigate,
    patientId,
    requiresScheduledAppointmentForNoteCreation,
  ]);

  return {
    isLoading,
    recordNow,
    requiresScheduledAppointmentForNoteCreation,
  };
};
